import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { PaginatedList } from "../types/paginated";
import type { CreateUserRequest, UserResponse } from "../types/user";
import { apiClient } from "./client";

export async function getMe(): Promise<UserResponse> {
  const res = await apiClient.get<UserResponse>("/me");
  return res.data;
}

export async function patchMe(data: { preferredLanguage: string }): Promise<UserResponse> {
  const res = await apiClient.patch<UserResponse>("/me", data);
  return res.data;
}

export interface ListUsersParams {
  role?: string;
  access?: string;
  search?: string;
  sortBy?: string;
  sortDir?: string;
  page?: number;
  limit?: number;
}

export async function getUsers(params?: ListUsersParams): Promise<PaginatedList<UserResponse>> {
  const res = await apiClient.get<PaginatedList<UserResponse>>("/users", { params });
  return res.data;
}

export async function getUserById(id: string): Promise<UserResponse> {
  const res = await apiClient.get<UserResponse>(`/users/${id}`);
  return res.data;
}

export function useMe() {
  return useQuery({
    queryKey: ["me"],
    queryFn: getMe,
  });
}

export function useUsers(params?: ListUsersParams, enabled = true) {
  return useQuery({
    queryKey: ["users", params],
    queryFn: () => getUsers(params),
    enabled,
  });
}

export function useUser(id: string) {
  return useQuery({
    queryKey: ["users", id],
    queryFn: () => getUserById(id),
    enabled: !!id,
  });
}

export async function createUser(data: CreateUserRequest): Promise<UserResponse> {
  const res = await apiClient.post<UserResponse>("/users", data, { skipErrorToast: true });
  return res.data;
}

export function useCreateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}

export async function getUserPicture(id: string): Promise<string | null> {
  try {
    const res = await apiClient.get<Blob>(`/users/${id}/picture`, {
      responseType: 'blob',
      skipErrorToast: true,
    })
    return URL.createObjectURL(res.data)
  } catch {
    return null
  }
}

export function useUserPicture(id: string | undefined) {
  return useQuery({
    queryKey: ['users', id, 'picture'],
    queryFn: () => getUserPicture(id!),
    enabled: !!id,
    staleTime: 60 * 60 * 1000,
  })
}

export async function patchUserAdmin(id: string, isAdmin: boolean): Promise<UserResponse> {
  const res = await apiClient.patch<UserResponse>(`/users/${id}`, { isAdmin });
  return res.data;
}

export function usePatchUserAdmin() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isAdmin }: { id: string; isAdmin: boolean }) => patchUserAdmin(id, isAdmin),
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ["users"] });
    },
  });
}
